/**
 * Search Drawer - Predictive search functionality
 */

class SearchDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-search-drawer]');
    if (!this.drawer) return;

    this.overlay = this.drawer.querySelector('[data-search-drawer-overlay]');
    this.input = this.drawer.querySelector('[data-search-input]');
    this.clearBtn = this.drawer.querySelector('[data-search-clear]');
    this.resultsContainer = this.drawer.querySelector('[data-search-results]');
    this.form = this.drawer.querySelector('[data-search-form]');
    this.panel = this.drawer.querySelector('.search-drawer__panel');

    // Templates
    this.resultsTemplate = document.querySelector('[data-search-results-template]');
    this.productTemplate = document.querySelector('[data-search-product-template]');
    this.noResultsTemplate = document.querySelector('[data-search-no-results-template]');

    this.debounceTimer = null;
    this.abortController = null;

    // Initialize focus trap
    this.focusTrap = null;
    if (window.FocusTrap && this.panel) {
      this.focusTrap = new window.FocusTrap(this.panel, {
        initialFocus: '[data-search-input]',
        onEscape: () => this.close()
      });
    }

    this.bindEvents();
  }

  bindEvents() {
    // Open search drawer
    document.querySelectorAll('[data-search-drawer-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close search drawer
    document.querySelectorAll('[data-search-drawer-close]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Close on overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // Close on escape key (fallback if focus trap not loaded)
    if (!this.focusTrap) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    // Search input
    this.input?.addEventListener('input', (e) => {
      this.handleSearch(e.target.value);
    });

    // Clear button
    this.clearBtn?.addEventListener('click', () => {
      this.input.value = '';
      this.clearBtn.style.display = 'none';
      this.showPlaceholder();
      this.input.focus();
    });

    // Form submit
    this.form?.addEventListener('submit', (e) => {
      if (!this.input.value.trim()) {
        e.preventDefault();
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Activate focus trap after animation (will focus input via initialFocus option)
    if (this.focusTrap) {
      setTimeout(() => this.focusTrap.activate(), 300);
    } else {
      // Fallback: Focus input after animation
      setTimeout(() => {
        this.input?.focus();
      }, 300);
    }
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Deactivate focus trap
    if (this.focusTrap) {
      this.focusTrap.deactivate();
    }
  }

  handleSearch(query) {
    // Show/hide clear button
    if (this.clearBtn) {
      this.clearBtn.style.display = query.length > 0 ? '' : 'none';
    }

    // Clear any pending search
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Cancel any in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }

    // Show placeholder if empty
    if (!query.trim()) {
      this.showPlaceholder();
      return;
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query.trim());
    }, 300);
  }

  async performSearch(query) {
    this.showLoading();

    this.abortController = new AbortController();

    try {
      // Try predictive search API first
      let products = await this.tryPredictiveSearch(query);

      // Fallback to search page scraping if predictive search fails
      if (!products) {
        products = await this.trySearchPageFetch(query);
      }

      // Flatten products into individual variants
      const variants = this.flattenToVariants(products || []);

      if (variants && variants.length > 0) {
        this.renderResults(query, variants);
      } else {
        this.showNoResults(query);
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        this.showNoResults(query);
      }
    }
  }

  async tryPredictiveSearch(query) {
    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) return null;

      const data = await response.json();
      return data.resources?.results?.products || null;
    } catch (e) {
      return null;
    }
  }

  async trySearchPageFetch(query) {
    try {
      const response = await fetch(
        `/search?q=${encodeURIComponent(query)}&type=product&view=json`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) return null;

      const html = await response.text();

      // Parse product data from the page
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productLinks = doc.querySelectorAll('.variant-card__link, .search-page__item a');

      const products = [];
      productLinks.forEach(link => {
        const titleEl = link.querySelector('.variant-card__title, h3');
        const priceEl = link.querySelector('.variant-card__price-current');
        const imgEl = link.querySelector('img');

        if (titleEl) {
          products.push({
            url: link.getAttribute('href'),
            title: titleEl.textContent.trim(),
            price: this.parsePriceText(priceEl?.textContent || '0'),
            image: imgEl?.getAttribute('src') || null
          });
        }
      });

      return products.length > 0 ? products.slice(0, 8) : null;
    } catch (e) {
      return null;
    }
  }

  parsePriceText(text) {
    const match = text.replace(/[^\d]/g, '');
    return parseInt(match, 10) || 0;
  }

  showPlaceholder() {
    const text = window.searchTranslations?.startTyping || 'Start typing to search';
    this.resultsContainer.innerHTML = `
      <div class="search-drawer__placeholder">
        <p>${text}</p>
      </div>
    `;
  }

  showLoading() {
    this.resultsContainer.innerHTML = `
      <div class="search-drawer__loading">
        <div class="search-drawer__spinner"></div>
      </div>
    `;
  }

  showNoResults(query) {
    const noResultsText = window.searchTranslations?.noResults?.replace('__QUERY__', this.escapeHtml(query)) || `No results found for "${this.escapeHtml(query)}"`;
    const tryDifferentText = window.searchTranslations?.tryDifferent || 'Try a different search term';

    if (!this.noResultsTemplate) {
      this.resultsContainer.innerHTML = `
        <div class="search-drawer__no-results">
          <p>${noResultsText}</p>
          <p class="search-drawer__no-results-hint">${tryDifferentText}</p>
        </div>
      `;
      return;
    }

    const template = this.noResultsTemplate.content.cloneNode(true);
    const noResultsTextEl = template.querySelector('[data-no-results-text]');
    if (noResultsTextEl) {
      noResultsTextEl.textContent = noResultsText.replace(/&quot;/g, '"');
    }
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.appendChild(template);
  }

  flattenToVariants(products) {
    const variants = [];
    products.forEach(product => {
      if (product.variants && product.variants.length > 0) {
        product.variants.forEach(variant => {
          if (variant.available === false) return;
          const imageUrl = variant.featured_image?.url || variant.image || product.image;
          variants.push({
            url: product.url + '?variant=' + variant.id,
            title: product.title,
            variantTitle: variant.title !== 'Default Title' ? variant.title : null,
            price: variant.price,
            image: imageUrl,
            available: variant.available
          });
        });
      } else {
        // Fallback for products without variant data (e.g. search page scrape)
        variants.push({
          url: product.url,
          title: product.title,
          variantTitle: null,
          price: product.price,
          image: product.image,
          available: true
        });
      }
    });
    return variants;
  }

  renderResults(query, products) {
    if (!products || products.length === 0) {
      this.showNoResults(query);
      return;
    }

    if (!this.resultsTemplate || !this.productTemplate) {
      this.renderResultsFallback(query, products);
      return;
    }

    const resultsEl = this.resultsTemplate.content.cloneNode(true);
    const productsContainer = resultsEl.querySelector('[data-products-container]');
    const countEl = resultsEl.querySelector('[data-results-count]');
    const viewAllLink = resultsEl.querySelector('[data-view-all-link]');

    // Set count
    countEl.textContent = `${products.length} result${products.length !== 1 ? 's' : ''}`;

    // Set view all link
    viewAllLink.href = `/search?q=${encodeURIComponent(query)}&type=product`;

    // Render variants
    products.forEach(product => {
      const productEl = this.productTemplate.content.cloneNode(true);
      const link = productEl.querySelector('[data-product-link]');
      const imageContainer = productEl.querySelector('[data-product-image]');
      const title = productEl.querySelector('[data-product-title]');
      const price = productEl.querySelector('[data-product-price]');

      link.href = product.url;
      title.textContent = product.variantTitle
        ? `${product.title} — ${product.variantTitle}`
        : product.title;
      price.textContent = this.formatMoney(product.price);

      if (product.image) {
        const img = document.createElement('img');
        img.src = this.getSizedImageUrl(product.image, '150x150');
        img.alt = product.title;
        img.loading = 'lazy';
        imageContainer.appendChild(img);
      }

      productsContainer.appendChild(productEl);
    });

    this.resultsContainer.innerHTML = '';
    this.resultsContainer.appendChild(resultsEl);
  }

  renderResultsFallback(query, products) {
    const viewAllText = window.searchTranslations?.viewAll || 'View all';
    let html = `
      <div class="search-drawer__results">
        <div class="search-drawer__results-header">
          <span>${products.length} result${products.length !== 1 ? 's' : ''}</span>
          <a href="/search?q=${encodeURIComponent(query)}&type=product" class="search-drawer__view-all">${viewAllText}</a>
        </div>
        <div class="search-drawer__products">
    `;

    products.forEach(product => {
      const imageHtml = product.image
        ? `<img src="${this.getSizedImageUrl(product.image, '150x150')}" alt="${this.escapeHtml(product.title)}" loading="lazy">`
        : '';

      const displayTitle = product.variantTitle
        ? `${this.escapeHtml(product.title)} — ${this.escapeHtml(product.variantTitle)}`
        : this.escapeHtml(product.title);

      html += `
        <a href="${product.url}" class="search-drawer__product">
          <div class="search-drawer__product-image">${imageHtml}</div>
          <div class="search-drawer__product-details">
            <span class="search-drawer__product-title">${displayTitle}</span>
            <span class="search-drawer__product-price">${this.formatMoney(product.price)}</span>
          </div>
        </a>
      `;
    });

    html += '</div></div>';
    this.resultsContainer.innerHTML = html;
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'JPY';
    // JPY doesn't use decimal places
    const amount = currency === 'JPY' ? cents : cents / 100;
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    // Convert Shopify image URL to sized version
    return url.replace(/\.([^.]+)$/, `_${size}.$1`);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  new SearchDrawer();
});
